import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, List, ListItem, ListItemButton, ListItemText, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { MillingJobType, MillingMaterial } from 'models/milling';
import { useMutation, useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import AddUpdateMillingJobTypeDialog from './add-update-milling-job-type-dialog.components';
import {
    millingJobTypesTemplate,
    millingMaterialsTemplate,
    addMillingJobTypeTemplate,
    deleteMillingJobTypeTemplate,
    modifyMillingJobTypeTemplate,
} from 'apollo/templates/milling';

const ManageMillingJobTypes: FC = () => {
    const { t: translate } = useTranslation(['milling-jobs']);
    const [isAddEditOpen, setIsAddEditOpen] = useState(false);
    const [currentJobType, setCurrentJobType] = useState<MillingJobType | undefined>();
    const { showBoundary } = useDenthubErrorBoundary();

    const {
        data: { millingJobTypes } = {},
        loading,
        refetch,
    } = useQuery(millingJobTypesTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });
    const { data: { millingMaterials } = {} } = useQuery(millingMaterialsTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [addMillingJobType] = useMutation(addMillingJobTypeTemplate, {
        onCompleted: () => {
            refetch();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [modifyMillingJobType] = useMutation(modifyMillingJobTypeTemplate, {
        onCompleted: () => {
            refetch();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteMillingJobType] = useMutation(deleteMillingJobTypeTemplate, {
        onCompleted: () => {
            refetch();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleDeleteJobTypeClick = (jobTypeId: string) => {
        deleteMillingJobType({ variables: { jobTypeId } });
    };

    const handleAddJobTypeClick = () => {
        setCurrentJobType(undefined);
        setIsAddEditOpen(true);
    };

    const handleEditJobTypeClick = (jobType: MillingJobType) => {
        setCurrentJobType(jobType);
        setIsAddEditOpen(true);
    };

    const handleAddJobType = (jobType: MillingJobType) => {
        jobType.materials = jobType.materials.map((jobType) => (jobType as MillingMaterial)._id as string);
        addMillingJobType({ variables: jobType });
        handleCloseAddEdit();
    };

    const handleUpdateJobType = (jobType: MillingJobType) => {
        jobType.materials = jobType.materials.map((jobType) => (jobType as MillingMaterial)._id as string);
        modifyMillingJobType({ variables: { ...jobType, jobTypeId: jobType._id } });
        handleCloseAddEdit();
    };

    const handleCloseAddEdit = () => {
        setIsAddEditOpen(false);
    };

    return (
        <Fragment>
            <Paper variant="outlined">
                {!loading && (
                    <List dense>
                        {millingJobTypes?.map((jobType: MillingJobType) => (
                            <ListItem
                                key={jobType._id}
                                secondaryAction={
                                    <IconButton
                                        onClick={() => handleDeleteJobTypeClick(jobType._id as string)}
                                        edge="end"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemButton onClick={() => handleEditJobTypeClick(jobType)}>
                                    <ListItemText primary={jobType.name} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Paper>
            <Box mt={1}>
                <Button onClick={() => handleAddJobTypeClick()} startIcon={<AddIcon />}>
                    {translate('milling-jobs:ADD_JOB_TYPE')}
                </Button>
            </Box>
            <AddUpdateMillingJobTypeDialog
                open={isAddEditOpen}
                jobType={currentJobType}
                onAdd={handleAddJobType}
                onUpdate={handleUpdateJobType}
                onDialogClose={handleCloseAddEdit}
                allMaterials={millingMaterials}
            />
        </Fragment>
    );
};

export default ManageMillingJobTypes;
